import { ObfuscatedGameState } from "@presidents/game-engine";
import type { InPlay } from "@presidents/game-engine/src/game";
import type { Turn } from "@presidents/game-engine/src/types";
import React, { useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import useInPlayAnimation from "../hooks/useInPlayAnimation";
import Card, { CardElement } from "./Card";

function isInPlayType(t: Turn): t is InPlay {
  return t.type === "trick";
}

const calcCardJitter = (i: number) => {
  let jitter = "";
  const move = Math.max(i % 2 ? 1 : 2);
  if (i % 5 === 0) {
    jitter += `translate(-${move}px, -${move}px)`;
  } else if (i % 4 === 0) {
    jitter += `translate(-${move}px, ${move}px)`;
  } else if (i % 3 === 0) {
    jitter += `translate(${move}px, -${move}px)`;
  } else if (i % 2 === 0) {
    jitter += `translate(${move}px, ${move}px)`;
  }
  if (i % 2 === 0) {
    jitter += " rotate(-1deg)";
  } else {
    jitter += " rotate(1deg)";
  }
  return jitter;
};

const offScreenKeyframes = keyframes`
    100% {
        transform: translateY(100vh);
    }
`;

const InPlayElement = styled.div<{ isHidden: boolean }>`
  position: relative;
  background-color: hsla(var(--layer3HSL), 0.5);
  backdrop-filter: blur(4px);
  width: var(--bottom-control-bar-height);
  height: var(--bottom-control-bar-height);
  border-radius: 10px;
  ${(props) =>
    props.isHidden &&
    css`
      display: none;
    `}
`;

const InPlayGroup = styled.div<{ jitter?: string }>`
  position: absolute;
  top: 50%;
  left: 50%;
  ${(props) => `transform: translate(-50%, -50%)${props.jitter}`};
  display: flex;
  & > ${CardElement}:not(:first-child) {
    margin-left: -20px;
  }
  &[data-last-turn] {
    animation: ${offScreenKeyframes} 0.7s 5s ease-in-out 1;
    animation-fill-mode: forwards;
  }
`;

type Props = {
  showCards: boolean;
  gameState?: ObfuscatedGameState;
};
const CardsInPlay: React.FC<Props> = ({ showCards, gameState }) => {
  const inPlayContainerRef = useRef<HTMLDivElement>(null);
  useInPlayAnimation(inPlayContainerRef, gameState);

  const showLastTurn =
    gameState?.state === "START_ROUND" &&
    gameState.lastTurn.filter(isInPlayType).length > 0;

  const [shouldShowLastTurn, setShouldShowLastTurn] = useState(false);
  useEffect(() => {
    if (showLastTurn) {
      setShouldShowLastTurn(true);
      const tid = window.setTimeout(() => {
        setShouldShowLastTurn(false);
      }, 7e3);
      return () => {
        setShouldShowLastTurn(false);
        window.clearTimeout(tid);
      };
    }
  }, [showLastTurn]);

  const cardsPlayed = shouldShowLastTurn
    ? gameState?.lastTurn.filter(isInPlayType)
    : gameState?.turns.filter(isInPlayType);

  return (
    <InPlayElement isHidden={!(showCards || shouldShowLastTurn)}>
      {cardsPlayed?.map(({ cards, __original_turn }: InPlay, i) => (
        <InPlayGroup
          data-last-turn={shouldShowLastTurn || undefined}
          ref={inPlayContainerRef}
          key={i}
          jitter={calcCardJitter(i)}
        >
          {(__original_turn?.cards || cards).map((card) => (
            <Card key={card._id} card={card} canSelect={false} />
          ))}
        </InPlayGroup>
      ))}
    </InPlayElement>
  );
};

export default CardsInPlay;
